import React, { useCallback } from "react";
import { connect } from "react-redux";

import { EntityType } from "@mapmycustomers/shared/enum";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";
import { MapSavedFilter } from "@mapmycustomers/shared/types/viewModel/SavedFilter";

import useMapView from "@app/component/view/utils/useMapView";
import { RootState } from "@app/store/rootReducer";
import { getSelectedFilter } from "@app/store/savedFilters";
import { deleteSavedFilter, selectSavedFilter } from "@app/store/savedFilters/actions";
import useAnalytics from "@app/util/contexts/useAnalytics";

import SelectedFilterState from "./SelectedFilterState";

interface Props {
  deleteSavedFilter: typeof deleteSavedFilter.request;
  entityType: EntityType.PIN;
  hideSavedFilters?: boolean;
  implicitFilterFields?: IField[];
  onCreate: () => void;
  selectedFilter?: MapSavedFilter;
  selectSavedFilter: typeof selectSavedFilter;
}

const SavedFilterState: React.FC<Props> = ({
  deleteSavedFilter,
  entityType,
  implicitFilterFields,
  onCreate,
  selectedFilter,
  selectSavedFilter,
}) => {
  const analytics = useAnalytics();
  const { updateViewState } = useMapView();

  const handleUpdateSavedFilter = useCallback(
    (savedFilter?: MapSavedFilter) => {
      updateViewState({ selectedSavedFilterId: savedFilter?.id });
      selectSavedFilter({ entityType, savedFilter });
    },
    [entityType, selectSavedFilter, updateViewState]
  );

  const handleDelete = useCallback(
    (savedFilter) => {
      analytics.clicked(["Delete Filter"], { filter: savedFilter });
      deleteSavedFilter(savedFilter);
    },
    [analytics, deleteSavedFilter]
  );

  if (selectedFilter) {
    return (
      <SelectedFilterState
        implicitFilterFields={implicitFilterFields}
        onCreate={onCreate}
        onDelete={handleDelete}
        onUpdateSavedFilter={handleUpdateSavedFilter}
        savedFilter={selectedFilter}
      />
    );
  }

  return null;
};

const mapStateToProps = (
  state: RootState,
  ownProps: { entityType: EntityType.PIN; hideSavedFilters?: boolean }
) => ({
  selectedFilter: ownProps.hideSavedFilters
    ? undefined
    : (getSelectedFilter(state)(ownProps.entityType) as MapSavedFilter | undefined),
});

const mapDispatchToProps = {
  deleteSavedFilter: deleteSavedFilter.request,
  selectSavedFilter,
};

export default connect(mapStateToProps, mapDispatchToProps)(SavedFilterState);
